(() => {
  const GLOBAL_KEY = '__landingReviewCarousel';
  const ROOT_SELECTOR = '[data-landing-review-carousel]';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const instances = new WeakMap();

  function initCarousel(root) {
    if (!root || instances.has(root)) return;

    const viewport = root.querySelector('[data-landing-review-viewport]');
    const track = root.querySelector('[data-landing-review-track]');
    const slides = Array.from(root.querySelectorAll('[data-landing-review-slide]'));
    const buttons = Array.from(root.querySelectorAll('[data-landing-review-pagination-button]'));

    if (!viewport || !track || slides.length < 2 || buttons.length !== slides.length) return;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listenerController = new AbortController();
    const leadingClone = slides[slides.length - 1].cloneNode(true);
    const trailingClone = slides[0].cloneNode(true);

    [leadingClone, trailingClone].forEach((clone) => {
      clone.removeAttribute('data-landing-review-slide');
      clone.removeAttribute('aria-label');
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.reviewClone = 'true';
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone
        .querySelectorAll('a, button, input, select, textarea, [tabindex]')
        .forEach((element) => element.setAttribute('tabindex', '-1'));
    });

    track.prepend(leadingClone);
    track.append(trailingClone);

    const physicalSlides = [leadingClone, ...slides, trailingClone];
    const defaultScrollBehavior = viewport.style.scrollBehavior;
    let activeIndex = 0;
    let animationFrame = 0;
    let settleTimer = 0;
    let restoreBehaviorFrame = 0;

    function setActive(index) {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));

      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === activeIndex;

        button.classList.toggle('is-active', active);
        if (active) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    function closestPhysicalIndex() {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      physicalSlides.forEach((slide, index) => {
        const distance = Math.abs(viewport.scrollLeft - slide.offsetLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    function logicalIndexForPhysical(physicalIndex) {
      if (physicalIndex === 0) return slides.length - 1;
      if (physicalIndex === physicalSlides.length - 1) return 0;
      return physicalIndex - 1;
    }

    function jumpToSlide(slide) {
      if (!slide) return;

      viewport.style.scrollBehavior = 'auto';
      viewport.scrollLeft = slide.offsetLeft;

      if (restoreBehaviorFrame) window.cancelAnimationFrame(restoreBehaviorFrame);
      restoreBehaviorFrame = window.requestAnimationFrame(() => {
        viewport.style.scrollBehavior = defaultScrollBehavior;
        restoreBehaviorFrame = 0;
      });
    }

    function settleLoop() {
      settleTimer = 0;
      const physicalIndex = closestPhysicalIndex();

      if (physicalIndex === 0) {
        setActive(slides.length - 1);
        jumpToSlide(slides[slides.length - 1]);
      } else if (physicalIndex === physicalSlides.length - 1) {
        setActive(0);
        jumpToSlide(slides[0]);
      }
    }

    function onScroll() {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(() => {
          animationFrame = 0;
          setActive(logicalIndexForPhysical(closestPhysicalIndex()));
        });
      }

      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settleLoop, 120);
    }

    function goToSlide(index) {
      const targetSlide = slides[index];
      if (!targetSlide) return;

      setActive(index);

      if (reducedMotionQuery.matches) {
        jumpToSlide(targetSlide);
        return;
      }

      viewport.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    function syncPosition() {
      jumpToSlide(slides[activeIndex]);
    }

    viewport.addEventListener('scroll', onScroll, {
      passive: true,
      signal: listenerController.signal
    });

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => goToSlide(index), {
        signal: listenerController.signal
      });
    });

    const resizeObserver = new ResizeObserver(() => {
      syncPosition();
    });

    resizeObserver.observe(viewport);
    syncPosition();

    instances.set(root, {
      destroy() {
        listenerController.abort();
        resizeObserver.disconnect();
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
        if (restoreBehaviorFrame) window.cancelAnimationFrame(restoreBehaviorFrame);
        if (settleTimer) window.clearTimeout(settleTimer);
        leadingClone.remove();
        trailingClone.remove();
        instances.delete(root);
      }
    });
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initCarousel(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initCarousel);
  }

  function destroyAll(scope) {
    if (!scope) return;

    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => instances.get(root)?.destroy());
  }

  window[GLOBAL_KEY] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
